import { useEffect, useState, useCallback } from "react";
import {
  subscribe, getQueue, getReadyFiles, pendingCount, readyFilesCount, getSyncing,
  isOnline, processQueue, deleteQueueItem, updateQueueItem, openReadyFile, deleteReadyFile, offlineReady,
} from "@/lib/offline";
import { cn } from "@/lib/utils";
import {
  Cloud, CloudOff, RefreshCw, Check, Trash2, Pencil, FileDown, X, AlertCircle, ListChecks, ShieldCheck, Loader2,
} from "lucide-react";

function OfflineReadyPill() {
  const [ready, setReady] = useState(null);
  useEffect(() => {
    const check = () => offlineReady().then(setReady);
    check();
    const unsub = subscribe(check);
    const t = setInterval(check, 10000);
    return () => { unsub(); clearInterval(t); };
  }, []);
  if (ready === null || !("serviceWorker" in navigator) || process.env.NODE_ENV !== "production") return null;
  return (
    <span data-testid="offline-ready-indicator" data-ready={ready ? "true" : "false"}
      title={ready ? "Uygulama internetsiz açılabilir" : "Çevrimdışı kullanım için hazırlanıyor — internet açıkken birkaç saniye bekleyin"}
      className={cn("h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-semibold",
        ready ? "bg-green-500/10 text-green-600 dark:text-green-400" : "bg-secondary text-muted-foreground")}>
      {ready ? <ShieldCheck size={16} /> : <Loader2 size={16} className="animate-spin" />}
      <span className="hidden md:inline">{ready ? "Çevrimdışı kullanıma hazır" : "Çevrimdışı hazırlanıyor"}</span>
    </span>
  );
}

export function useSyncState() {
  const [state, setState] = useState({ online: isOnline(), syncing: getSyncing(), pending: 0, ready: 0 });
  const refresh = useCallback(async () => {
    const [pending, ready] = await Promise.all([pendingCount(), readyFilesCount()]);
    setState({ online: isOnline(), syncing: getSyncing(), pending, ready });
  }, []);
  useEffect(() => {
    refresh();
    const unsub = subscribe(refresh);
    const on = () => refresh();
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    return () => { unsub(); window.removeEventListener("online", on); window.removeEventListener("offline", on); };
  }, [refresh]);
  return state;
}

const fieldLabel = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function EditForm({ item, onDone }) {
  const data = item.data && typeof item.data === "object" ? item.data : {};
  const primitiveKeys = Object.keys(data).filter((k) => ["string", "number", "boolean"].includes(typeof data[k]) && !k.startsWith("_"));
  const [vals, setVals] = useState(() => Object.fromEntries(primitiveKeys.map((k) => [k, data[k]])));

  const save = async () => {
    const merged = { ...data };
    for (const k of primitiveKeys) {
      const orig = data[k];
      let v = vals[k];
      if (typeof orig === "number") v = v === "" ? 0 : Number(v);
      if (typeof orig === "boolean") v = !!v;
      merged[k] = v;
    }
    await updateQueueItem(item.id, { data: merged });
    onDone();
  };

  if (primitiveKeys.length === 0) {
    return (
      <div className="mt-2 text-xs text-muted-foreground">
        Bu işlem için düzenlenebilir alan yok. İsterseniz silip tekrar oluşturabilirsiniz.
        <div className="mt-2 flex justify-end"><button onClick={onDone} className="text-xs font-medium px-2 py-1 rounded hover:bg-secondary">Kapat</button></div>
      </div>
    );
  }
  return (
    <div className="mt-2 space-y-2" data-testid={`sync-edit-${item.id}`}>
      {primitiveKeys.map((k) => (
        <div key={k}>
          <label className="block text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-0.5">{fieldLabel(k)}</label>
          {typeof data[k] === "boolean" ? (
            <input type="checkbox" checked={!!vals[k]} onChange={(e) => setVals((s) => ({ ...s, [k]: e.target.checked }))} className="accent-primary" data-testid={`sync-field-${k}`} />
          ) : (
            <input
              type={typeof data[k] === "number" ? "number" : "text"}
              value={vals[k] ?? ""}
              onChange={(e) => setVals((s) => ({ ...s, [k]: e.target.value }))}
              data-testid={`sync-field-${k}`}
              className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs focus:ring-2 focus:ring-primary outline-none"
            />
          )}
        </div>
      ))}
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onDone} className="text-xs font-medium px-2.5 py-1 rounded-full hover:bg-secondary">İptal</button>
        <button onClick={save} data-testid={`sync-save-${item.id}`} className="text-xs font-semibold px-3 py-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</button>
      </div>
    </div>
  );
}

export default function SyncManager() {
  const { online, syncing, pending, ready } = useSyncState();
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [files, setFiles] = useState([]);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    const [q, f] = await Promise.all([getQueue(), getReadyFiles()]);
    setQueue(q); setFiles(f);
  }, []);

  useEffect(() => { if (open) reload(); }, [open, pending, ready, syncing, reload]);

  const badge = pending > 0 ? pending : ready;
  const statusText = !online ? "Çevrimdışı" : syncing ? "Yükleniyor" : pending > 0 ? "Bekliyor" : ready > 0 ? "Belge Hazır" : "Senkron";
  const pillTone = !online
    ? "bg-destructive/10 text-destructive"
    : syncing || pending > 0
      ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
      : ready > 0
        ? "bg-sky-500/10 text-sky-600 dark:text-sky-400"
        : "bg-green-500/10 text-green-600 dark:text-green-400";

  return (
    <div className="relative flex items-center gap-1.5">
      <OfflineReadyPill />
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="sync-status-button"
        title={online ? (syncing ? "Senkronize ediliyor" : "Çevrimiçi") : "Çevrimdışı"}
        className={cn("relative h-9 px-2.5 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition-colors", pillTone)}
      >
        {!online ? <CloudOff size={16} /> : syncing ? <RefreshCw size={16} className="animate-spin" /> : pending > 0 ? <Cloud size={16} /> : ready > 0 ? <FileDown size={16} /> : <Check size={16} />}
        <span className="hidden sm:inline" data-testid="sync-status-text">{statusText}</span>
        {badge > 0 && (
          <span data-testid="sync-pending-count" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">{badge}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[22rem] max-w-[90vw] rounded-xl border border-border bg-popover shadow-xl z-50 overflow-hidden" data-testid="sync-panel">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListChecks size={16} className="text-primary" />
                <span className="font-head font-semibold text-sm">Senkronizasyon</span>
              </div>
              <span className={cn("text-[11px] font-semibold px-2 py-0.5 rounded-full", online ? "bg-green-500/10 text-green-600 dark:text-green-400" : "bg-destructive/10 text-destructive")}>
                {online ? "Çevrimiçi" : "Çevrimdışı"}
              </span>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {/* Bekleyen işlemler */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">Bekleyen İşlemler ({queue.length})</span>
                  {online && queue.length > 0 && (
                    <button onClick={() => processQueue()} data-testid="sync-now-button" className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                      <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> Şimdi Senkronize Et
                    </button>
                  )}
                </div>
                {queue.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">Bekleyen işlem yok. Tüm veriler senkron. ✅</p>
                ) : (
                  <div className="space-y-2">
                    {queue.map((it) => (
                      <div key={it.id} className="rounded-lg border border-border bg-card p-2.5" data-testid={`sync-item-${it.id}`}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{it.label}</div>
                            <div className="text-[10px] text-muted-foreground">{new Date(it.ts).toLocaleString("tr-TR")}</div>
                            {it.status === "error" && (
                              <div className="mt-1 flex items-start gap-1 text-[10px] text-destructive"><AlertCircle size={11} className="shrink-0 mt-0.5" /><span>{it.error}</span></div>
                            )}
                          </div>
                          {it.kind !== "media" && it.kind !== "file" && (
                            <button onClick={() => setEditing(editing === it.id ? null : it.id)} data-testid={`sync-edit-btn-${it.id}`} className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary transition-colors" title="Düzenle">
                              <Pencil size={13} />
                            </button>
                          )}
                          <button onClick={async () => { await deleteQueueItem(it.id); reload(); }} data-testid={`sync-delete-btn-${it.id}`} className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors" title="Sil">
                            <Trash2 size={13} />
                          </button>
                        </div>
                        {editing === it.id && <EditForm item={it} onDone={() => { setEditing(null); reload(); }} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hazır belgeler */}
              {files.length > 0 && (
                <div className="px-4 py-3 border-t border-border">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">Hazır Belgeler ({files.length})</div>
                  <div className="space-y-2">
                    {files.map((f) => (
                      <div key={f.id} className="flex items-center gap-2 rounded-lg border border-border bg-card p-2.5" data-testid={`sync-file-${f.id}`}>
                        <FileDown size={15} className="text-primary shrink-0" />
                        <div className="flex-1 min-w-0 text-xs font-medium truncate">{f.label}</div>
                        <button onClick={() => openReadyFile(f.id)} data-testid={`sync-file-open-${f.id}`} className="text-[11px] font-semibold text-primary hover:underline">Aç</button>
                        <button onClick={async () => { await deleteReadyFile(f.id); reload(); }} className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-secondary transition-colors" title="Sil"><X size={13} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-border bg-secondary/30 text-[10px] text-muted-foreground leading-relaxed">
              İnternet olmadan girdiğiniz kayıtlar burada tutulur, bağlantı gelince otomatik yüklenir.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
